import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { PiPencilSimpleDuotone, PiPlusBold, PiTrashDuotone } from 'react-icons/pi';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { isSuperAdmin } from '@v2/lib/auth';
import type { Announcement, CompanyEvent } from '@v2/types/intranet';
import { EVENT_TYPE_LABELS } from '@v2/types/intranet';

// ─── shared ──────────────────────────────────────────────────────────────────

const inputCls = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b p-4">
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

function DeleteConfirm({ label, onConfirm, onCancel }: { label: string; onConfirm: () => void; onCancel: () => void }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl space-y-4">
                <p className="text-sm text-gray-700">Remover <strong>{label}</strong>? Esta ação é irreversível.</p>
                <div className="flex gap-3 justify-end">
                    <Button variant="outline" size="sm" onClick={onCancel}>Cancelar</Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={onConfirm}>Remover</Button>
                </div>
            </div>
        </div>
    );
}

// ─── Announcements tab ────────────────────────────────────────────────────────

type AnnForm = { title: string; content: string; type: string; pinned: boolean; active: boolean; published_at: string; expires_at: string };

function AnnouncementsTab() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState<Announcement | null | 'new'>(null);
    const [deleting, setDeleting] = useState<Announcement | null>(null);

    const { data: items = [], isLoading } = useQuery<Announcement[]>({
        queryKey: ['intranet', 'announcements', 'all'],
        queryFn: () => api.get('/intranet/announcements/all').then((r) => r.data.data),
    });

    const { register, handleSubmit, reset } = useForm<AnnForm>();

    const openNew = () => { reset({ type: 'notice', pinned: false, active: true, published_at: '', expires_at: '', title: '', content: '' }); setEditing('new'); };
    const openEdit = (a: Announcement) => { reset({ title: a.title, content: a.content ?? '', type: a.type, pinned: a.pinned, active: a.active, published_at: a.published_at?.slice(0, 16) ?? '', expires_at: a.expires_at?.slice(0, 16) ?? '' }); setEditing(a); };

    const saveMutation = useMutation({
        mutationFn: (data: AnnForm) => {
            const payload = { ...data, published_at: data.published_at || null, expires_at: data.expires_at || null };
            if (editing === 'new') return api.post('/intranet/announcements', payload);
            return api.put(`/intranet/announcements/${(editing as Announcement).id}`, payload);
        },
        onSuccess: () => { toast.success('Aviso salvo.'); qc.invalidateQueries({ queryKey: ['intranet'] }); setEditing(null); },
        onError: () => toast.error('Erro ao salvar.'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => api.delete(`/intranet/announcements/${id}`),
        onSuccess: () => { toast.success('Aviso removido.'); qc.invalidateQueries({ queryKey: ['intranet'] }); setDeleting(null); },
        onError: () => toast.error('Erro ao remover.'),
    });

    if (isLoading) return <LoadingState />;

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <Button size="sm" onClick={openNew}><PiPlusBold className="mr-1.5 h-4 w-4" /> Novo aviso</Button>
            </div>

            {items.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">Nenhum aviso cadastrado.</p>
            ) : (
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                    {items.map((a) => (
                        <div key={a.id} className="flex items-center gap-3 p-3">
                            <div className="flex-1 min-w-0">
                                <p className="truncate text-sm font-medium text-gray-900">{a.title}</p>
                                <p className="text-xs text-gray-400">
                                    {a.type_label} · {a.pinned ? 'Fixado · ' : ''}{a.active ? 'Ativo' : 'Inativo'}
                                </p>
                            </div>
                            <button onClick={() => openEdit(a)} className="p-1.5 text-gray-400 hover:text-primary">
                                <PiPencilSimpleDuotone className="h-4 w-4" />
                            </button>
                            <button onClick={() => setDeleting(a)} className="p-1.5 text-gray-400 hover:text-red-500">
                                <PiTrashDuotone className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <Modal title={editing === 'new' ? 'Novo aviso' : 'Editar aviso'} onClose={() => setEditing(null)}>
                    <form onSubmit={handleSubmit((d) => saveMutation.mutate(d))} className="space-y-3">
                        <div>
                            <label className={labelCls}>Título *</label>
                            <input {...register('title', { required: true })} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Conteúdo</label>
                            <textarea {...register('content')} className={inputCls} rows={4} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Tipo</label>
                                <select {...register('type')} className={inputCls}>
                                    <option value="notice">Aviso</option>
                                    <option value="info">Informação</option>
                                    <option value="warning">Atenção</option>
                                    <option value="alert">Alerta</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-4 pt-5">
                                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                    <input type="checkbox" {...register('pinned')} className="rounded" />
                                    Fixar
                                </label>
                                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                    <input type="checkbox" {...register('active')} className="rounded" />
                                    Ativo
                                </label>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Publicar em</label>
                                <input type="datetime-local" {...register('published_at')} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Expira em</label>
                                <input type="datetime-local" {...register('expires_at')} className={inputCls} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <Button variant="outline" size="sm" type="button" onClick={() => setEditing(null)}>Cancelar</Button>
                            <Button size="sm" type="submit" disabled={saveMutation.isPending}>
                                {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}

            {deleting && (
                <DeleteConfirm
                    label={deleting.title}
                    onConfirm={() => deleteMutation.mutate(deleting.id)}
                    onCancel={() => setDeleting(null)}
                />
            )}
        </div>
    );
}

// ─── Events tab ───────────────────────────────────────────────────────────────

type EvtForm = { title: string; description: string; event_date: string; end_date: string; all_day: boolean; type: string; color: string };

function EventsTab() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState<CompanyEvent | null | 'new'>(null);
    const [deleting, setDeleting] = useState<CompanyEvent | null>(null);

    const { data: items = [], isLoading } = useQuery<CompanyEvent[]>({
        queryKey: ['intranet', 'events', 'all'],
        queryFn: () => api.get('/intranet/events/all').then((r) => r.data.data),
    });

    const { register, handleSubmit, reset } = useForm<EvtForm>();

    const openNew = () => { reset({ type: 'event', all_day: true, title: '', description: '', event_date: '', end_date: '', color: '' }); setEditing('new'); };
    const openEdit = (e: CompanyEvent) => { reset({ title: e.title, description: e.description ?? '', event_date: e.event_date, end_date: e.end_date ?? '', all_day: e.all_day, type: e.type, color: '' }); setEditing(e); };

    const saveMutation = useMutation({
        mutationFn: (data: EvtForm) => {
            const payload = { ...data, end_date: data.end_date || null, color: data.color || null };
            if (editing === 'new') return api.post('/intranet/events', payload);
            return api.put(`/intranet/events/${(editing as CompanyEvent).id}`, payload);
        },
        onSuccess: () => { toast.success('Evento salvo.'); qc.invalidateQueries({ queryKey: ['intranet'] }); setEditing(null); },
        onError: () => toast.error('Erro ao salvar.'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => api.delete(`/intranet/events/${id}`),
        onSuccess: () => { toast.success('Evento removido.'); qc.invalidateQueries({ queryKey: ['intranet'] }); setDeleting(null); },
        onError: () => toast.error('Erro ao remover.'),
    });

    if (isLoading) return <LoadingState />;

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <Button size="sm" onClick={openNew}><PiPlusBold className="mr-1.5 h-4 w-4" /> Novo evento</Button>
            </div>

            {items.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">Nenhum evento cadastrado.</p>
            ) : (
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                    {items.map((e) => (
                        <div key={e.id} className="flex items-center gap-3 p-3">
                            <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: e.color }} />
                            <div className="flex-1 min-w-0">
                                <p className="truncate text-sm font-medium text-gray-900">{e.title}</p>
                                <p className="text-xs text-gray-400">{e.event_date} · {e.type_label}</p>
                            </div>
                            <button onClick={() => openEdit(e)} className="p-1.5 text-gray-400 hover:text-primary">
                                <PiPencilSimpleDuotone className="h-4 w-4" />
                            </button>
                            <button onClick={() => setDeleting(e)} className="p-1.5 text-gray-400 hover:text-red-500">
                                <PiTrashDuotone className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <Modal title={editing === 'new' ? 'Novo evento' : 'Editar evento'} onClose={() => setEditing(null)}>
                    <form onSubmit={handleSubmit((d) => saveMutation.mutate(d))} className="space-y-3">
                        <div>
                            <label className={labelCls}>Título *</label>
                            <input {...register('title', { required: true })} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Descrição</label>
                            <textarea {...register('description')} className={inputCls} rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Data início *</label>
                                <input type="date" {...register('event_date', { required: true })} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Data fim</label>
                                <input type="date" {...register('end_date')} className={inputCls} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Tipo</label>
                                <select {...register('type')} className={inputCls}>
                                    {Object.entries(EVENT_TYPE_LABELS).map(([v, l]) => (
                                        <option key={v} value={v}>{l}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>Cor (hex, opcional)</label>
                                <input type="text" {...register('color')} placeholder="#f97316" className={inputCls} />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" {...register('all_day')} className="rounded" />
                            Dia inteiro
                        </label>
                        <div className="flex justify-end gap-2 pt-1">
                            <Button variant="outline" size="sm" type="button" onClick={() => setEditing(null)}>Cancelar</Button>
                            <Button size="sm" type="submit" disabled={saveMutation.isPending}>
                                {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}

            {deleting && (
                <DeleteConfirm
                    label={deleting.title}
                    onConfirm={() => deleteMutation.mutate(deleting.id)}
                    onCancel={() => setDeleting(null)}
                />
            )}
        </div>
    );
}

// ─── Documents tab ────────────────────────────────────────────────────────────

// Documentos agora têm editor visual, versões e aprovação: a edição fica na própria tela de documentos.
function DocumentsTab() {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <p>Os documentos são criados e editados em <strong>Empresa → Documentos</strong>, com editor visual, histórico de versões, controle de acesso e aprovação.</p>
            <div className="mt-4 flex gap-2">
                <Link to="/company/documents">
                    <Button variant="outline">Ver documentos</Button>
                </Link>
                <Link to="/company/documents/new">
                    <Button>
                        <PiPlusBold className="me-1.5 size-4" />
                        Novo documento
                    </Button>
                </Link>
            </div>
        </div>
    );
}

// ─── main page ────────────────────────────────────────────────────────────────

const TABS = [
    { key: 'announcements', label: 'Avisos' },
    { key: 'events',        label: 'Eventos' },
    { key: 'documents',     label: 'Documentos' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export default function IntranetConfigPage() {
    const [tab, setTab] = useState<TabKey>('announcements');

    if (!isSuperAdmin()) return <Navigate to="/" replace />;

    return (
        <div className="space-y-6">
            <PageHeader title="Intranet — Configuração" breadcrumb={[{ name: 'Configurações' }, { name: 'Intranet' }]} />

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex gap-6">
                    {TABS.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            className={`border-b-2 pb-3 text-sm font-medium transition ${
                                tab === key
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </nav>
            </div>

            {tab === 'announcements' && <AnnouncementsTab />}
            {tab === 'events'        && <EventsTab />}
            {tab === 'documents'     && <DocumentsTab />}
        </div>
    );
}
